import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

type EmptyStateVariant = 'muted' | 'ghost' | 'plain';
type EmptyStateSize = 'default' | 'compact';
type EmptyStateAlign = 'center' | 'start';

type EmptyStateProps = {
  className?: string;
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  variant?: EmptyStateVariant;
  size?: EmptyStateSize;
  align?: EmptyStateAlign;
};

const VARIANT_STYLES: Record<EmptyStateVariant, string> = {
  muted: 'rounded-lg border border-dashed border-border/60 bg-muted/40',
  ghost: 'rounded-lg border border-dashed border-border/40 bg-muted/20',
  plain: 'rounded-lg border border-dashed border-border/40 bg-background',
};

const SIZE_STYLES: Record<EmptyStateSize, string> = {
  default: 'px-6 py-8 gap-3 text-sm',
  compact: 'px-4 py-4 gap-2 text-sm',
};

const ALIGN_STYLES: Record<EmptyStateAlign, string> = {
  center: 'items-center text-center',
  start: 'items-start text-left',
};

export function EmptyState({
  className,
  icon,
  title,
  description,
  actions,
  children,
  variant = 'muted',
  size = 'default',
  align = 'center',
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex w-full flex-col justify-center text-muted-foreground',
        VARIANT_STYLES[variant],
        SIZE_STYLES[size],
        ALIGN_STYLES[align],
        className,
      )}
    >
      {icon ? <div className="text-2xl text-muted-foreground">{icon}</div> : null}
      {title ? <h3 className="text-base font-medium text-foreground">{title}</h3> : null}
      {description ? <p className="max-w-prose text-sm text-muted-foreground">{description}</p> : null}
      {children}
      {actions ? <div className="flex flex-wrap items-center justify-center gap-2">{actions}</div> : null}
    </div>
  );
}

export type { EmptyStateProps };
